(() => {
  'use strict';

  const SOURCE_ID = 'luxxri-catalog-promo-source';
  const TEMPLATE_SELECTOR = '.luxxri-catalog-promo-template';
  const PROMO_SELECTOR = '[data-luxxri-catalog-promo]';
  const VIDEO_SELECTOR = '[data-luxxri-promo-video]';

  /*
    ELLA uses .productListing in most collection layouts.
    Other selectors are lightweight fallbacks only.
  */
  const GRID_SELECTORS = [
    '.productListing',
    '#main-collection-product-grid',
    '[data-product-grid]',
    '[data-products-grid]',
    '.products-grid',
    '.product-grid'
  ];

  let grid = null;
  let observer = null;
  let observerRoot = null;
  let videoObserver = null;
  let lastSignature = '';
  let renderScheduled = false;


  /* ---------------------------------------------------------
     Promo templates
     --------------------------------------------------------- */

  function getPromoTemplates() {
    const source = document.getElementById(SOURCE_ID);

    if (!source) {
      return [];
    }

    return Array.from(
      source.querySelectorAll(TEMPLATE_SELECTOR)
    )
      .map(template => ({
        template,
        position: Number.parseInt(
          template.dataset.position || '0',
          10
        )
      }))
      .filter(
        item =>
          Number.isInteger(item.position) &&
          item.position > 0
      )
      .sort(
        (a, b) => a.position - b.position
      );
  }


  /* ---------------------------------------------------------
     Product grid detection
     --------------------------------------------------------- */

  function isProductCard(element) {
    if (!(element instanceof Element)) {
      return false;
    }

    if (element.matches(PROMO_SELECTOR)) {
      return false;
    }

    return Boolean(
      element.matches('a[href*="/products/"]') ||
      element.querySelector('a[href*="/products/"]')
    );
  }


  function getProductCards(targetGrid) {
    if (!targetGrid) {
      return [];
    }

    return Array.from(
      targetGrid.children
    ).filter(isProductCard);
  }


  function findProductGrid() {
    for (const selector of GRID_SELECTORS) {
      const candidates =
        document.querySelectorAll(selector);

      for (const candidate of candidates) {
        if (getProductCards(candidate).length >= 5) {
          return candidate;
        }
      }
    }

    return null;
  }


  /* ---------------------------------------------------------
     Product sequence tracking
     --------------------------------------------------------- */

  function getProductSignature(products) {
    return products
      .map(product => {
        const link = product.matches(
          'a[href*="/products/"]'
        )
          ? product
          : product.querySelector(
              'a[href*="/products/"]'
            );

        return link
          ? link.getAttribute('href') || ''
          : '';
      })
      .join('|');
  }


  /* ---------------------------------------------------------
     Lazy promo video loading
     --------------------------------------------------------- */

  function loadPromoVideo(video) {
    if (
      !(video instanceof HTMLVideoElement) ||
      video.dataset.luxxriVideoLoaded === 'true'
    ) {
      return;
    }

    const sources = video.querySelectorAll(
      'source[data-src]'
    );

    if (!sources.length) {
      return;
    }

    sources.forEach(source => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    video.dataset.luxxriVideoLoaded = 'true';
    video.load();
  }


  function playPromoVideo(video) {
    loadPromoVideo(video);

    const playPromise = video.play();

    if (
      playPromise &&
      typeof playPromise.catch === 'function'
    ) {
      playPromise.catch(() => {});
    }
  }


  function setupPromoVideos(targetGrid) {
    if (!targetGrid) {
      return;
    }

    const videos = targetGrid.querySelectorAll(
      VIDEO_SELECTOR
    );

    if (!videos.length) {
      return;
    }

    /*
      Respect reduced-motion preferences.
      Shopify preview image remains visible as
      the static poster instead.
    */
    if (
      window.matchMedia &&
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      return;
    }

    /*
      Lightweight fallback for older browsers.
    */
    if (!('IntersectionObserver' in window)) {
      videos.forEach(playPromoVideo);
      return;
    }

    if (!videoObserver) {
      videoObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            const video = entry.target;

            if (!(video instanceof HTMLVideoElement)) {
              return;
            }

            if (entry.isIntersecting) {
              playPromoVideo(video);
            } else if (
              video.dataset.luxxriVideoLoaded === 'true'
            ) {
              video.pause();
            }
          });
        },
        {
          root: null,
          /*
            Begin loading shortly before the promo
            reaches the viewport.
          */
          rootMargin: '300px 0px',
          threshold: 0
        }
      );
    }

    videos.forEach(video => {
      videoObserver.observe(video);
    });
  }


  /* ---------------------------------------------------------
     Promo rendering
     --------------------------------------------------------- */

  function removeExistingPromos(targetGrid) {
    const promos = targetGrid.querySelectorAll(
      `:scope > ${PROMO_SELECTOR}`
    );

    promos.forEach(promo => {
      if (videoObserver) {
        promo
          .querySelectorAll(VIDEO_SELECTOR)
          .forEach(video => {
            videoObserver.unobserve(video);
          });
      }

      promo.remove();
    });
  }


  function renderPromos(force = false) {
    renderScheduled = false;

    const templates = getPromoTemplates();

    if (!templates.length) {
      return;
    }

    /*
      Re-find only if the current grid disappeared,
      e.g. after an ELLA sort/filter replacement.
    */
    if (!grid || !grid.isConnected) {
      grid = findProductGrid();
    }

    if (!grid) {
      return;
    }

    const products =
      getProductCards(grid);

    if (!products.length) {
      return;
    }

    const signature =
      getProductSignature(products);

    /*
      If Show More / sorting hasn't changed the
      actual product sequence, do nothing.
    */
    if (
      !force &&
      signature === lastSignature
    ) {
      ensureObserver();
      setupPromoVideos(grid);
      return;
    }

    removeExistingPromos(grid);

    for (const item of templates) {
      if (products.length < item.position) {
        continue;
      }

      const targetProduct =
        products[item.position - 1];

      if (!targetProduct) {
        continue;
      }

      targetProduct.after(
        item.template.content.cloneNode(true)
      );
    }

    lastSignature = signature;

    ensureObserver();
    setupPromoVideos(grid);
  }


  /* ---------------------------------------------------------
     Mutation filtering
     --------------------------------------------------------- */

  function nodeContainsProduct(node) {
    if (!(node instanceof Element)) {
      return false;
    }

    /*
      Ignore our own banner elements completely.
    */
    if (
      node.matches(PROMO_SELECTOR) ||
      node.closest(PROMO_SELECTOR)
    ) {
      return false;
    }

    return Boolean(
      node.matches('a[href*="/products/"]') ||
      node.querySelector('a[href*="/products/"]')
    );
  }


  function mutationHasProductChange(
    mutation
  ) {
    for (const node of mutation.addedNodes) {
      if (nodeContainsProduct(node)) {
        return true;
      }
    }

    for (const node of mutation.removedNodes) {
      if (nodeContainsProduct(node)) {
        return true;
      }
    }

    return false;
  }


  /* ---------------------------------------------------------
     Render scheduling
     --------------------------------------------------------- */

  function scheduleRender() {
    if (renderScheduled) {
      return;
    }

    renderScheduled = true;

    /*
      Coalesces multiple ELLA DOM changes into
      one browser-frame update.
    */
    requestAnimationFrame(() => {
      renderPromos(false);
    });
  }


  /* ---------------------------------------------------------
     Scoped product-grid observer
     --------------------------------------------------------- */

  function getObserverRoot() {
    if (!grid) {
      return null;
    }

    return (
      grid.closest(
        '.halo-collection-content, .collection-template, [data-section-id]'
      ) ||
      grid.parentElement ||
      grid
    );
  }


  function ensureObserver() {
    const newRoot =
      getObserverRoot();

    if (!newRoot) {
      return;
    }

    if (
      observer &&
      observerRoot === newRoot
    ) {
      return;
    }

    if (observer) {
      observer.disconnect();
    }

    observerRoot = newRoot;

    observer = new MutationObserver(
      mutations => {
        if (
          mutations.some(
            mutationHasProductChange
          )
        ) {
          /*
            If ELLA replaced the grid itself,
            invalidate the cached reference.
          */
          if (
            grid &&
            !grid.isConnected
          ) {
            grid = null;
          }

          scheduleRender();
        }
      }
    );

    observer.observe(
      observerRoot,
      {
        childList: true,
        subtree: true
      }
    );
  }


  /* ---------------------------------------------------------
     Initialization
     --------------------------------------------------------- */

  function init() {
    grid = findProductGrid();

    if (!grid) {
      return;
    }

    renderPromos(true);
  }


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }


  /*
    Theme editor support only.
    Normal shoppers do not trigger this event.
  */
  document.addEventListener(
    'shopify:section:load',
    () => {
      grid = null;
      lastSignature = '';
      renderPromos(true);
    }
  );
})();